import { makeStyles } from '@material-ui/styles'
import { imageUrlRoot } from 'app/constants'
import { routerDoc } from 'app/docLinks'
import ExternalLink from 'core/components/ExternalLink'
import PicklistField from 'core/components/validatedForm/DropdownField'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import TextField from 'core/components/validatedForm/TextField'
import ModalForm from 'core/elements/modal/ModalForm'
import ModalFormHeaders from 'core/elements/modal/ModalFormHeaders'
import MultiValuesField from 'core/elements/MultiValuesField'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { Route } from 'core/plugins/route'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import ServiceUnhealthyInfo from 'openstack/components/common/ServiceUnhealthyInfo'
import { useCallback } from 'react'
import useReactRouter from 'use-react-router'
import { listAllAvailablePorts } from '../actions'
import AdminStatePicklist from '../AdminStatePicklist'
import PortPicklist from '../network-details/PortPicklist'
import SubnetPicklist from '../network-details/SubnetPicklist'
import { createRouter, createRouterInterface } from './actions'
import ExternalNetworkPicklist from './ExternalNetworkPicklist'

const useStyles = makeStyles<Theme>((theme) => ({
  dropdown: {
    minWidth: '352px',
  },
  portSection: {
    marginTop: theme.spacing(2),
  },
  header: {
    marginBottom: theme.spacing(3),
  },
}))

interface Props {
  addRoute: Route
}

export default function CreateRouterModal({ addRoute }: Props) {
  const { history, location } = useReactRouter()
  const classes = useStyles()
  const searchParams = new URLSearchParams(location.search)
  const fromLoadBalanceMember = searchParams.get('fromLoadBalanceMember')

  const defaultParams = {
    name: '',
    adminState: 'Up',
    externalNetwork: '',
    subnet: fromLoadBalanceMember ? [{}, {}] : [],
    port: [],
  }
  const imagePath = `${imageUrlRoot}/router.svg`
  const { params, getParamsUpdater, setParams } = useParams(defaultParams)
  const { update, updating, error, reset } = useUpdateAction(createRouter)
  const {
    bulkUpdate: createInterfaceFn,
    updating: createInterfaceUpdating,
    error: createInterfaceError,
    reset: resetInterface,
  } = useUpdateAction(createRouterInterface)
  const { reload: reloadPorts } = useListAction(listAllAvailablePorts)

  // create interfaces
  const createRouterInterfaces = useCallback(
    async (routerId: string) => {
      if (!routerId) {
        return { interfaceSuccess: true }
      }

      const subnetInterfaces = params?.subnet
        ?.filter((subnet: any) => !!subnet?.id)
        ?.map(({ id }: any) => ({
          id: routerId,
          body: { subnet_id: id },
        }))
      const portInterfaces = params?.port
        ?.filter((port) => !!port?.id)
        ?.map(({ id }) => ({ id: routerId, body: { port_id: id } }))

      // create interfaces
      const interfaces = await createInterfaceFn([...subnetInterfaces, ...portInterfaces])
      const interfaceSuccess = interfaces.every((int) => int?.success)

      return { interfaceSuccess }
    },
    [params, createInterfaceFn],
  )

  const submitForm = useCallback(async () => {
    const body = {
      router: {
        name: params.name,
        admin_state_up: params.adminState !== 'Down',
        external_gateway_info: !!params.externalNetwork
          ? {
              network_id: params.externalNetwork,
            }
          : undefined,
      },
    }
    const { success, response } = await update({ body })
    const { interfaceSuccess } = await createRouterInterfaces(response?.id)

    if (success && interfaceSuccess) {
      reloadPorts(true, true)
      handleClose()
      reset()
      resetInterface()
    }
  }, [params, update, createRouterInterfaces, reloadPorts])

  const handleClose = () => {
    setParams(defaultParams)
    reset()
    resetInterface()
    history.push(routeManager.routes.openstack.routers.path())
  }

  return (
    <ModalForm
      route={addRoute}
      title={`Create Router`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={updating || createInterfaceUpdating}
      error={error || createInterfaceError}
      submitTitle={`Create Router`}
      maxWidth={464}
    >
      <>
        <ServiceUnhealthyInfo action="Router Creation" network />
        <ModalFormHeaders
          imagePath={imagePath}
          message={
            <>
              Use a <ExternalLink url={routerDoc}>router</ExternalLink> to connect networks and
              enable communication between them.
            </>
          }
          className={classes.header}
        />
        <FormFieldSection title="Router Configuration" alignContentLeft>
          <TextField
            id="name"
            label="Name"
            onChange={getParamsUpdater('name')}
            value={params.name}
            required
          />
          <AdminStatePicklist value={params.adminState} onChange={getParamsUpdater('adminState')} />
          <ExternalNetworkPicklist
            value={params.externalNetwork}
            onChange={getParamsUpdater('externalNetwork')}
            includeNoneOption
          />
        </FormFieldSection>
        <FormFieldSection
          title="Interface Configuration"
          alignContentLeft
          className={classes.interfaceConfiguration}
        >
          <MultiValuesField
            id="subnet"
            label="Subnet"
            info="Add subnets to route traffic between networks."
            items={params.subnet}
            addLabel="Add Subnet"
            onChange={getParamsUpdater('subnet')}
            Component={(props) => (
              <PicklistField
                id="subnet"
                label=""
                DropdownComponent={SubnetPicklist}
                className={classes.dropdown}
                {...props}
              />
            )}
          />
          <MultiValuesField
            id="port"
            label="Port"
            info="Add ports to route traffic between networks."
            items={params.port}
            addLabel="Add Port"
            onChange={getParamsUpdater('port')}
            className={classes.portSection}
            Component={(props) => (
              <PicklistField
                id="port"
                label=""
                DropdownComponent={PortPicklist}
                className={classes.dropdown}
                {...props}
              />
            )}
          />
        </FormFieldSection>
      </>
    </ModalForm>
  )
}
